import { Head, Link, router } from "@inertiajs/react"
import {
  ArrowUpRight,
  Clock,
  Copy,
  Database,
  Download,
  Edit,
  ExternalLink,
  Eye,
  FolderArchive,
  HardDrive,
  Plus,
} from "lucide-react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

import { ConfigWarnings } from "@/components/config-warnings"
import Heading from "@/components/heading"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { formatBytes, getEnvironmentColor } from "@/lib/utils"
import {
  appBackupPath,
  appBackupsPath,
  appPath,
  downloadAppBackupPath,
  duplicateAppPath,
  editAppPath,
  editBackupConfigPath,
  serverPath,
  serversPath,
} from "@/routes"
import type { BreadcrumbItem, DiskUsageSnapshot } from "@/types"
import type { App, Backup, BackupConfig, Server } from "@/types"

interface AppShowProps {
  app: App & {
    server: Pick<Server, "id" | "name">
    backup_configs: Pick<
      BackupConfig,
      | "id"
      | "backup_type"
      | "destination_type"
      | "frequency"
      | "enabled"
      | "source_paths"
      | "destination_path"
    >[]
    backups: (Pick<
      Backup,
      "id" | "status" | "frequency_type" | "file_size" | "completed_at" | "file_path"
    > & {
      backup_config: Pick<
        BackupConfig,
        "id" | "backup_type" | "destination_type" | "source_paths"
      >
    })[]
  }
  app_size: number | null
  serverDiskSnapshot?: DiskUsageSnapshot | null
  warnings: {
    severity: "error" | "warning" | "info"
    title: string
    message: string
    action_text?: string
    action_path?: string
  }[]
}

export default function AppShow({
  app,
  app_size,
  serverDiskSnapshot,
  warnings,
}: AppShowProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Servers", href: serversPath() },
    { title: app.server.name, href: serverPath(app.server.id) },
    { title: app.name, href: appPath(app.id) },
  ]

  function handleDuplicate() {
    const newName = prompt(
      "Enter a name for the duplicated app:",
      `${app.name} (copy)`,
    )
    if (newName) {
      router.post(duplicateAppPath(app.id), { new_name: newName })
    }
  }

  function formatDate(date: string | null | undefined): string {
    if (!date) return "Never"
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={app.name} />

      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex flex-col items-start gap-3">
            <Heading
              title={app.name}
              description={app.location || "Application overview"}
              marginBottom={false}
            />
            <div className="flex items-center gap-3">

              <Badge variant={app.app_type === "rails" ? "default" : "secondary"}>
                {app.app_type === "rails" ? "Rails" : "Static"}
              </Badge>
              {app.environment && (
                <Badge
                  style={{
                    backgroundColor: getEnvironmentColor(app.environment),
                    color: "white",
                    borderColor: getEnvironmentColor(app.environment),
                  }}
                >
                  {app.environment}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDuplicate}>
              <Copy className="mr-2 h-4 w-4" />
              Duplicate
            </Button>
            <Button variant="outline" asChild>
              <Link href={editAppPath(app.id)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          </div>
        </div>

        {warnings && warnings.length > 0 && (
          <ConfigWarnings warnings={warnings} className="mt-6" />
        )}

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Application Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {app.app_url && (
                <div>
                  <dt className="text-muted-foreground text-sm font-medium">
                    Primary URL
                  </dt>
                  <dd className="mt-1">
                    <a
                      href={app.app_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary flex items-center gap-1 hover:underline"
                    >
                      {app.app_url}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </dd>
                </div>
              )}

              {app.location && (
                <div>
                  <dt className="text-muted-foreground text-sm font-medium">
                    Location
                  </dt>
                  <dd className="mt-1 font-mono text-sm">{app.location}</dd>
                </div>
              )}

              {app_size !== null && app_size !== undefined && (
                <div>
                  <dt className="text-muted-foreground text-sm font-medium">
                    Application Size
                  </dt>
                  <dd className="mt-1 flex items-center gap-2">
                    <HardDrive className="h-4 w-4" />
                    {formatBytes(app_size)}
                  </dd>
                  {serverDiskSnapshot &&
                    serverDiskSnapshot.total_bytes &&
                    app_size > 0 && (
                      <dd className="mt-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">
                            Server Usage
                          </span>
                          <span className="text-muted-foreground">
                            {(
                              (app_size / serverDiskSnapshot.total_bytes) *
                              100
                            ).toFixed(2)}
                            % of server
                          </span>
                        </div>
                        <div className="bg-secondary mt-1 h-2 w-full overflow-hidden rounded-full">
                          <div
                            className="bg-primary h-full transition-all"
                            style={{
                              width: `${Math.min(
                                (app_size / serverDiskSnapshot.total_bytes) *
                                  100,
                                100,
                              )}%`,
                            }}
                          />
                        </div>
                        <p className="text-muted-foreground mt-1 text-xs">
                          {formatBytes(app_size)} of{" "}
                          {formatBytes(serverDiskSnapshot.total_bytes)}
                        </p>
                      </dd>
                    )}
                </div>
              )}

              {app.db_type && (
                <div>
                  <dt className="text-muted-foreground text-sm font-medium">
                    Database
                  </dt>
                  <dd className="mt-1 flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    {app.db_type} {app.db_name && `(${app.db_name})`}
                  </dd>
                </div>
              )}

              {app.other_urls && (
                <div>
                  <dt className="text-muted-foreground text-sm font-medium">
                    Other URLs
                  </dt>
                  <dd className="mt-1 space-y-1">
                    {app.other_urls.split(",").map((url, i) => (
                      <a
                        key={i}
                        href={url.trim()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary flex items-center gap-1 text-sm hover:underline"
                      >
                        {url.trim()}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ))}
                  </dd>
                </div>
              )}

              {app.notification_recipients && (
                <div>
                  <dt className="text-muted-foreground text-sm font-medium">
                    Notification Recipients
                  </dt>
                  <dd className="mt-1 flex flex-wrap gap-1">
                    {app.notification_recipients.split(",").map((email, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {email.trim()}
                      </Badge>
                    ))}
                  </dd>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Backup Configs */}
          <Card
            id="backup-configs"
            className={`scroll-mt-24 ${window.location.hash.match("#backup-configs") ? "bg-primary/2 shadow-lg" : ""}`}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Backup Configuration</CardTitle>
                <Button variant="outline" size="sm" asChild>
                  <Link href={`/apps/${app.id}/backup_configs/new`}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {app.backup_configs.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No backup configurations
                </p>
              ) : (
                <div className="space-y-3">
                  {app.backup_configs.map((config) => (
                    <div
                      key={config.id}
                      className="flex flex-col gap-3 rounded-lg border p-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-1">
                          {config.backup_type === "database" ? (
                            <Database className="text-muted-foreground h-5 w-5" />
                          ) : (
                            <FolderArchive className="text-muted-foreground h-5 w-5" />
                          )}
                          <p className="font-medium capitalize">
                            {config.backup_type}
                          </p>
                          <Badge variant="secondary">{config.frequency}</Badge>
                          <Badge variant="secondary">
                            {config.destination_type}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-3">
                          <Badge variant={config.enabled ? "default" : "secondary"}>
                            {config.enabled ? "Enabled" : "Disabled"}
                          </Badge>
                          <Button variant="ghost" size="sm" asChild>
                            <Link href={editBackupConfigPath(config.id)}>
                              <Edit className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </div>

                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium">Source:</span>{" "}
                        {config.backup_type == "directory" && config.source_paths && config.source_paths.length > 0 && (
                          config.source_paths.join(", ")
                        )}
                        {config.backup_type == "database" && (
                          <span>{app.db_name} ({app.db_type})</span>
                        )}
                      </div>
                      {config.destination_path && (
                        <div className="text-xs text-muted-foreground">
                          <span className="font-medium">Destination:</span>{" "}
                          {config.destination_path}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Backups */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex-1">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <CardTitle>Recent Backups</CardTitle>
                  <CardDescription>Last 10 backup runs</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0"
                  asChild
                >
                  <Link href={appBackupsPath(app.id)}>
                    <ArrowUpRight className="mr-2 h-4 w-4" />
                    View All
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {app.backups.length === 0 ? (
                <p className="text-muted-foreground text-sm">No backups yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="pb-2 text-left font-medium">Type</th>
                        <th className="pb-2 text-left font-medium">
                          Frequency
                        </th>
                        <th className="pb-2 text-left font-medium">Status</th>
                        <th className="pb-2 text-left font-medium">Size</th>
                        <th className="pb-2 text-left font-medium">
                          Completed
                        </th>
                        <th className="pb-2 text-right font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {app.backups.map((backup) => (
                        <tr key={backup.id} className="border-b last:border-0">
                          <td className="py-2">
                            <div className="flex flex-col gap-0.5">
                              <span className="flex items-center gap-2">
                                {backup.backup_config?.backup_type ===
                                "database" ? (
                                  <Database className="text-muted-foreground h-4 w-4" />
                                ) : (
                                  <FolderArchive className="text-muted-foreground h-4 w-4" />
                                )}
                                {backup.backup_config?.backup_type ?? "—"}
                              </span>
                              {(backup.backup_config?.backup_type ===
                                "database"
                                ? app.db_name && app.db_type
                                : backup.backup_config?.source_paths?.length) && (
                                <span className="text-muted-foreground text-xs">
                                  {backup.backup_config?.backup_type ===
                                  "database"
                                    ? `${app.db_name} (${app.db_type})`
                                    : backup.backup_config?.source_paths?.join(
                                        ", ",
                                      )}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2 capitalize">
                            {backup.frequency_type}
                          </td>
                          <td className="py-2">
                            <Badge
                              variant={
                                backup.status === "success"
                                  ? "default"
                                  : backup.status === "error"
                                    ? "destructive"
                                    : backup.status === "running"
                                      ? "outline"
                                      : "secondary"
                              }
                            >
                              {backup.status}
                            </Badge>
                          </td>
                          <td className="py-2">
                            <span className="flex items-center gap-1">
                              <HardDrive className="h-3 w-3" />
                              {formatBytes(backup.file_size)}
                            </span>
                          </td>
                          <td className="py-2">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDate(backup.completed_at)}
                            </span>
                          </td>
                          <td className="py-2 text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="icon" asChild>
                                <Link href={appBackupPath(app.id, backup.id)}>
                                  <Eye className="h-4 w-4" />
                                </Link>
                              </Button>
                              {backup.status === "success" &&
                                backup.file_path && (
                                  <Button variant="ghost" size="icon" asChild>
                                    <a
                                      href={downloadAppBackupPath(
                                        app.id,
                                        backup.id,
                                      )}
                                    >
                                      <Download className="h-4 w-4" />
                                    </a>
                                  </Button>
                                )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {app.notes && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {app.notes}
                  </ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
